/**
 * BHG Creator-Ref Cookie Capture
 * ------------------------------
 * When a visitor lands on any BHG URL with `?ref=<creator_slug>`, we set a
 * `bhg_ref` cookie with 60-day TTL. The /go/<vendor> edge function reads
 * this cookie and credits the creator's affiliate code on outbound clicks
 * via the GoAffPro `?ref=<value>` param.
 *
 * This is the ONLY mechanism that closes the loop when a creator pastes a
 * clean BHG URL (without UTMs) into their bio/post — the visitor lands,
 * cookie sets, then any outbound /go click for the next 60 days credits the
 * creator. UTM-based attribution (utm_source=creator&utm_campaign=<slug>)
 * still works in parallel and overrides the cookie when both are present.
 *
 * Why a cookie instead of localStorage:
 *   - The /go edge function runs on the Supabase Edge runtime — it can read
 *     `Cookie` request headers but has no access to localStorage.
 *   - Cookies are sent with same-origin sub-requests automatically, so
 *     `/go/bhg-labs` clicks carry `bhg_ref` without any client-side wiring.
 *
 * Slug validation: only lowercase alphanumeric + hyphens, 3-32 chars. We
 * silently drop anything else so an attacker can't smuggle XSS/cookie
 * injection via `?ref=<script>` style payloads.
 */

const COOKIE_NAME = "bhg_ref";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 60; // 60 days
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

/**
 * Set the bhg_ref cookie. SameSite=Lax so it travels with the visitor's
 * own clicks but doesn't leak in cross-site embeds. Secure so it's only
 * sent over HTTPS — production www.bodyhackguide.co is HTTPS-only.
 */
function writeCookie(slug: string) {
  if (typeof document === "undefined") return;
  const isHttps = typeof location !== "undefined" && location.protocol === "https:";
  const parts = [
    `${COOKIE_NAME}=${encodeURIComponent(slug)}`,
    "Path=/",
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  if (isHttps) parts.push("Secure");
  document.cookie = parts.join("; ");
}

/**
 * Read the bhg_ref cookie. Returns the slug if valid, null otherwise.
 * Used by client-side share buttons + creator dashboard so they can
 * surface "you're sharing as <slug>" UI when the cookie is present.
 */
export function getCreatorRef(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)bhg_ref=([^;]+)/);
  if (!match) return null;
  const slug = decodeURIComponent(match[1]).toLowerCase();
  if (!SLUG_PATTERN.test(slug)) return null;
  return slug;
}

/**
 * Clear the cookie. Useful for "stop crediting <creator>" links in a
 * creator's own dashboard preview ("see what your link looks like to a
 * fresh visitor"), or for QA/manual testing.
 */
export function clearCreatorRef() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Capture `?ref=<slug>` from the current URL. Only writes the cookie if
 * the slug is well-formed. Called on every route change from RouteListeners
 * so a visitor who lands on /blog/post then later clicks /tools/coa with
 * ?ref=mike still gets credited (not just the first landing page).
 *
 * Returns the slug if captured (so callers can fire analytics events), null
 * otherwise.
 */
export function captureCreatorRef(search: string): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(search);
  const ref = params.get("ref");
  if (!ref) return null;
  const slug = ref.toLowerCase().trim();
  if (!SLUG_PATTERN.test(slug)) return null;
  writeCookie(slug);
  return slug;
}
